import React, { Component, ErrorInfo, ReactNode, useEffect, useState } from 'react';
import { RefreshCcw, ArrowLeft, Home } from 'lucide-react';
import { withTranslation, WithTranslation, useTranslation } from 'react-i18next';

const HOME_URL = 'https://www.libritui.com';
const CHUNK_RELOAD_KEY = 'libritui:chunk-reload';

// Erros de chunk (deploy novo, rede instável) deixam a tela branca: tentamos 1 reload antes de mostrar o erro.
const isChunkError = (error: Error | null) =>
  !!error && /Loading chunk|dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(error.message || '');

const reloadOnceForChunk = (): boolean => {
  try {
    if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return true;
    }
  } catch { /* storage bloqueado: mostra a tela de erro */ }
  return false;
};

// O Vite dispara este evento quando um import dinâmico falha (chunk antigo após deploy).
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForChunk()) event.preventDefault();
  });
  // Boot bem-sucedido por 10 s: libera um novo reload automático no futuro.
  window.setTimeout(() => { try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ } }, 10000);
}

/**
 * Tela de erro padrão (Design 2.0): usada pelo boundary global, pelo boundary de cada página
 * e quando um carregamento trava. "Voltar ao Home" sempre leva a www.libritui.com.
 */
export const ErrorScreen: React.FC<{ onBack?: () => void; onReload?: () => void }> = ({ onBack, onReload }) => {
  const { t } = useTranslation();
  const safeT = (key: string, fallback: string) => {
    const res = t(key);
    return res === key ? fallback : res;
  };

  return (
    <div className="min-h-screen min-h-[100dvh] w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 relative">
      <button
        type="button"
        onClick={onBack || (() => window.history.back())}
        aria-label={safeT('global.back', 'Voltar')}
        className="group absolute top-6 left-6 inline-flex items-center justify-center w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone transition-colors duration-300 cursor-pointer"
      >
        <ArrowLeft size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
      </button>

      <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] p-8 md:p-10 rounded-[28px] max-w-sm w-full text-center animate-in fade-in zoom-in-95 duration-500">
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-3">
          {safeT('error.label', 'Erro')}
        </p>
        <h2 className="text-2xl font-medium tracking-[-0.03em] text-ink mb-2">
          {safeT('error.oops', 'Ops! Algo falhou')}
        </h2>
        <p className="text-[13px] leading-relaxed text-slate-500 dark:text-slate-400 mb-8">
          {safeT('error.loadProblem', 'Encontramos um problema ao carregar esta tela. Mas não se preocupe, seus dados estão a salvo.')}
        </p>
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => { window.location.href = HOME_URL; }}
            className="group w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-slate-900 text-[13px] font-medium rounded-full hover:opacity-90 active:scale-[0.98] transition-all duration-300 cursor-pointer"
          >
            <Home size={15} strokeWidth={1.75} />
            {safeT('error.backHome', 'Voltar ao Home')}
          </button>
          <button
            type="button"
            onClick={onReload || (() => window.location.reload())}
            className="group w-full flex items-center justify-center gap-2 py-3.5 bg-transparent text-ink text-[13px] font-medium rounded-full border border-black/[0.08] dark:border-white/[0.08] hover:bg-tint-stone active:scale-[0.98] transition-all duration-300 cursor-pointer"
          >
            <RefreshCcw size={15} strokeWidth={1.75} className="transition-transform duration-500 group-hover:rotate-180" />
            {safeT('error.reloadScreen', 'Recarregar tela')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Fallback do Suspense: spinner discreto e, se a tela não carregar em `timeoutMs`,
 * troca pela tela de erro (evita a tela branca/vazia eterna).
 */
export const LoadingOrError: React.FC<{ timeoutMs?: number; label?: string }> = ({ timeoutMs = 20000, label }) => {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(id);
  }, [timeoutMs]);

  if (timedOut) return <ErrorScreen />;
  return (
    <div className="h-screen h-[100dvh] w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="flex flex-col items-center gap-5 animate-in fade-in duration-700">
        <svg className="lt-mark lt-anim w-16 h-16" viewBox="-2 -4 104 104" aria-hidden="true"><use href="#lt-bird" /></svg>
        {label && <p className="font-mono text-[10px] text-slate-400 uppercase tracking-[0.14em]">{label}</p>}
      </div>
    </div>
  );
};

interface Props extends WithTranslation {
  children?: ReactNode;
  /** Quando muda (ex.: a rota), o boundary se recupera sozinho. */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class GlobalErrorBoundaryInner extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error in UI:", error, errorInfo);
    if (isChunkError(error)) reloadOnceForChunk();
  }

  public componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoBack = () => {
    this.setState({ hasError: false, error: null });
    window.history.back();
  };

  public render() {
    if (this.state.hasError) {
      return <ErrorScreen onBack={this.handleGoBack} onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}

export const GlobalErrorBoundary = withTranslation()(GlobalErrorBoundaryInner);
